import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  ArrowSquareOut,
  EnterFullScreen,
  ExitFullScreen,
  Phone,
  ResizeHandle,
  VideoCamera,
  X,
} from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { captureSentryException } from "@/lib/sentry";
import { openExternalUrl } from "@/lib/open-url";
import { useDocPip } from "./use-doc-pip";
import { PipIcon, PipPlaceholder } from "./pip-presentational";
import {
  clampToViewport,
  defaultRect,
  VIEWPORT_PAD,
  type DialogRect,
} from "./call-dialog-geometry";

// Lazy import keeps the LiveKit JS SDK (~200KB gzip) out of the
// initial bundle. The first `await import` hits when the user actually
// opens a call.
const CallSurface = lazy(() => import("./CallSurface"));

interface InAppCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDisconnected?: () => void;
  onPopOut?: () => Promise<boolean> | boolean;
  /** LiveKit WebSocket URL — drives the native in-app surface. When
   *  null (token fetch failed, server not configured), the dialog
   *  shows a connecting / fallback state. */
  serverUrl: string | null;
  /** LiveKit JWT — drives the native in-app surface. */
  token: string | null;
  /** Pre-built `meet.livekit.io/custom?…` URL kept exclusively as the
   *  "Open in browser" escape hatch. NOT loaded into an iframe — the
   *  in-app surface uses the SDK directly. */
  joinUrl: string | null;
  mode: "audio" | "video" | null;
  channelName: string;
}

/**
 * Floating in-app call window. Pre-LiveKit-embed this dialog hosted an
 * iframe that loaded LiveKit's hosted Meet page. Now it hosts a native
 * `<CallSurface>` that joins the same room directly via the JS SDK.
 *
 * Drag + resize are custom (not browser-native `resize:both`):
 *   • Drag: header is the handle. Mousedown on header (excluding the
 *     icon buttons) → document-level mousemove tracks pointer delta →
 *     mouseup ends. Pointer goes anywhere in the OS viewport;
 *     positions are clamped on each move so the close button stays
 *     reachable.
 *   • Resize: bottom-right corner has a visible diagonal-stripe
 *     affordance that mousedown-handles a width/height drag. Browser
 *     `resize:both` was abandoned because the rounded-corner border
 *     hid the native handle.
 *
 * Web pages can't escape their own viewport; Electron uses a native
 * BrowserWindow for true detached movement.
 */
export function InAppCallDialog({
  open,
  onOpenChange,
  onDisconnected,
  onPopOut,
  serverUrl,
  token,
  joinUrl,
  mode,
  channelName,
}: InAppCallDialogProps) {
  const [expanded, setExpanded] = useState(false);
  const [rect, setRect] = useState<DialogRect>(defaultRect);
  const pip = useDocPip();
  // PiP target swaps move the DOM but preserve the React tree, so the
  // LiveKit room connection stays alive across web PiP transitions.
  const [inlineHost, setInlineHost] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onResize = () => setRect((r) => clampToViewport(r));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  const closeDialog = useCallback(() => {
    setExpanded(false);
    pip.close();
    onOpenChange(false);
  }, [onOpenChange, pip]);

  const handlePopOut = useCallback(() => {
    if (onPopOut) {
      void onPopOut();
      return;
    }
    if (pip.isOpen) {
      pip.close();
      return;
    }
    void pip.open({ width: rect.width, height: rect.height + 32 });
  }, [onPopOut, pip, rect.height, rect.width]);

  // "Open in browser" is the escape hatch. We MUST close the in-app
  // session first — otherwise both the in-app SDK connection and the
  // browser tab try to join LiveKit with the same identity, and the
  // server kicks one of them. Default LiveKit behavior is "newest wins"
  // which silently disconnects the in-app session and leaves the user
  // wondering why their call ended.
  const handleOpenInBrowser = useCallback(() => {
    if (!joinUrl) return;
    closeDialog();
    openExternalUrl(joinUrl);
  }, [closeDialog, joinUrl]);

  const handleLiveKitError = useCallback(
    (error: Error) => {
      captureSentryException(error, {
        tag: "livekit-call",
        extra: { mode },
      });
      toast.error("Couldn't connect to the call. Try opening in browser.", {
        action: joinUrl
          ? { label: "Open in browser", onClick: handleOpenInBrowser }
          : undefined,
      });
      closeDialog();
    },
    [closeDialog, joinUrl, mode, handleOpenInBrowser],
  );

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDialog();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, closeDialog]);

  // Drag + resize implementations. We track pointer deltas against
  // the rect at gesture-start time so re-renders don't drift the
  // position. Listeners attach to `document` so the user can drag
  // past the dialog edges without losing capture.
  //
  // The pending-cleanup ref + unmount effect close a real bug: the
  // mousemove/mouseup listeners used to be added inside the handler
  // and removed on mouseup. If the dialog unmounted mid-drag (call
  // ends from another path while the user holds the mouse down), the
  // mouseup never fires on this component and the listeners leak,
  // calling setRect on an unmounted instance forever.
  const dragStateRef = useRef<{
    pointerStartX: number;
    pointerStartY: number;
    rectStartLeft: number;
    rectStartTop: number;
  } | null>(null);
  const resizeStateRef = useRef<{
    pointerStartX: number;
    pointerStartY: number;
    rectStartWidth: number;
    rectStartHeight: number;
  } | null>(null);
  const pendingListenersRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      pendingListenersRef.current?.();
      pendingListenersRef.current = null;
    };
  }, []);

  const startDrag = useCallback(
    (
      onMove: (ev: MouseEvent) => void,
      cleanupRef: React.MutableRefObject<unknown>,
    ) => {
      // Tear down any prior gesture's listeners before attaching new
      // ones (defensive — shouldn't happen because mouseup always
      // runs first, but guarantees idempotency).
      pendingListenersRef.current?.();
      const onUp = () => {
        cleanupRef.current = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        pendingListenersRef.current = null;
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      pendingListenersRef.current = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        cleanupRef.current = null;
      };
    },
    [],
  );

  const handleHeaderMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Skip drag when the click is on a button inside the header so
      // expand / close / open-in-browser still work cleanly.
      if (
        e.target instanceof Element &&
        e.target.closest("button, [data-no-drag]")
      ) {
        return;
      }
      if (expanded) return; // No drag in fullscreen mode
      e.preventDefault();
      dragStateRef.current = {
        pointerStartX: e.clientX,
        pointerStartY: e.clientY,
        rectStartLeft: rect.left,
        rectStartTop: rect.top,
      };
      startDrag((ev) => {
        const s = dragStateRef.current;
        if (!s) return;
        setRect((r) =>
          clampToViewport({
            ...r,
            left: s.rectStartLeft + (ev.clientX - s.pointerStartX),
            top: s.rectStartTop + (ev.clientY - s.pointerStartY),
          }),
        );
      }, dragStateRef);
    },
    [expanded, rect.left, rect.top, startDrag],
  );

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (expanded) return;
      e.preventDefault();
      e.stopPropagation();
      resizeStateRef.current = {
        pointerStartX: e.clientX,
        pointerStartY: e.clientY,
        rectStartWidth: rect.width,
        rectStartHeight: rect.height,
      };
      startDrag((ev) => {
        const s = resizeStateRef.current;
        if (!s) return;
        setRect((r) =>
          clampToViewport({
            ...r,
            width: s.rectStartWidth + (ev.clientX - s.pointerStartX),
            height: s.rectStartHeight + (ev.clientY - s.pointerStartY),
          }),
        );
      }, resizeStateRef);
    },
    [expanded, rect.height, rect.width, startDrag],
  );

  if (!open) return null;

  const positionStyle: React.CSSProperties = expanded
    ? {
        top: VIEWPORT_PAD,
        left: VIEWPORT_PAD,
        right: VIEWPORT_PAD,
        bottom: VIEWPORT_PAD,
      }
    : {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      };

  return (
    <div
      className="fixed z-50 rounded-xl border border-border bg-background overflow-hidden flex flex-col shadow-lg"
      style={positionStyle}
    >
      {/* Header — also the drag handle. Cursor flips between grab/grabbing
          via `cursor-grab` so the affordance is discoverable. */}
      <div
        className={`h-9 px-3 border-b border-border flex items-center justify-between shrink-0 select-none ${
          expanded ? "" : "cursor-grab active:cursor-grabbing"
        }`}
        onMouseDown={handleHeaderMouseDown}
      >
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground min-w-0">
          {mode === "audio" ? (
            <Phone size={14} className="text-success shrink-0" />
          ) : (
            <VideoCamera size={14} className="text-success shrink-0" />
          )}
          <span className="truncate">
            {mode === "audio" ? "Audio" : mode === "video" ? "Video" : "Call"}{" "}
            call · #{channelName || "channel"}
          </span>
        </div>
        <div className="flex items-center gap-0.5" data-no-drag>
          {(onPopOut || pip.supported) && !expanded && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={handlePopOut}
              title={
                onPopOut
                  ? "Open in separate window"
                  : pip.isOpen
                    ? "Return to in-app window"
                    : "Pop out to a window"
              }
            >
              <PipIcon size={14} />
            </Button>
          )}
          {joinUrl && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={handleOpenInBrowser}
              title="Open in browser"
            >
              <ArrowSquareOut size={14} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? "Minimize" : "Expand"}
            disabled={pip.isOpen}
          >
            {expanded ? (
              <ExitFullScreen size={14} />
            ) : (
              <EnterFullScreen size={14} />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            onClick={closeDialog}
            title="End call"
          >
            <X size={14} />
          </Button>
        </div>
      </div>

      {/* Call content. The CallSurface itself is mounted ONCE, via
          createPortal to whichever target is currently active. When
          PiP opens, the inline placeholder takes over this area and
          the same CallSurface DOM is moved (not remounted) into the
          PiP window's body. */}
      <div className="flex-1 bg-overlay/50 min-h-0 relative">
        {/* Inline host — the portal target while PiP is closed. We
            keep it mounted at all times so its ref stays attached;
            it's just hidden when PiP is open so the placeholder can
            occupy the space without overlap. */}
        <div
          ref={setInlineHost}
          className={pip.isOpen ? "hidden" : "h-full w-full"}
        />

        {pip.isOpen && <PipPlaceholder onReturn={pip.close} />}

        {!(serverUrl && token && mode) && !pip.isOpen && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground gap-2">
            <LoadingIcon size={16} />
            Connecting…
          </div>
        )}

        {serverUrl &&
          token &&
          mode &&
          (pip.isOpen ? pip.pipDocument?.body : inlineHost) &&
          createPortal(
            <div
              className={
                pip.isOpen
                  ? "fixed inset-0 bg-background text-foreground"
                  : "h-full w-full"
              }
            >
              <Suspense
                fallback={
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground gap-2">
                    <LoadingIcon size={16} />
                    Connecting…
                  </div>
                }
              >
                <CallSurface
                  serverUrl={serverUrl as string}
                  token={token as string}
                  mode={mode as "audio" | "video"}
                  onDisconnected={onDisconnected ?? closeDialog}
                  onError={handleLiveKitError}
                />
              </Suspense>
            </div>,
            (pip.isOpen ? pip.pipDocument?.body : inlineHost) as Element,
          )}

        {/* Resize handle (bottom-right). Hidden in fullscreen mode.
            The visual is a small diagonal-stripe square so the
            affordance is discoverable without relying on the
            invisible browser-native handle. */}
        {!expanded && (
          <div
            className="absolute bottom-0 right-0 size-3.5 cursor-nwse-resize"
            onMouseDown={handleResizeMouseDown}
            title="Resize"
            aria-label="Resize call window"
          >
            <ResizeHandle size={14} className="text-muted-foreground/60" />
          </div>
        )}
      </div>
    </div>
  );
}
